import * as yup from 'yup';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  paramsFinish,
  paramsNext,
  bankReferencesField,
  bankReferencesElement,
  paramsAdd,
  IBVSetup,
  slimTier
} from '../constants/form';
import { fileSumSizeLimit } from '../constants/files';
import { baseField } from '../constants/base';
import { envSetting } from '../constants/envSetting';
import { siteMap, routes } from '../constants/routes';
import { endpoints } from '../constants/endpoints';
import {
  isUndefinedNull,
  trimLowerCase,
  validateEmail,
  validateLength,
  validURL,
  removeFromFormObject
} from '../helpers/shared';
import { validateCountry } from '../helpers/base';
import { validateRequestType } from '../helpers/formData';
import { userTier, defaultChecked } from '../helpers/menu';
import { moduleCategories, tierMigratable } from '../helpers/tier';
import { getSchemaTier, isModuleHidden } from '../helpers/form';
import { userFilesAndMigrateBifrost, migrateBifrost } from '../redux/actions/tier'
import { setMessage } from '../redux/actions/message'
import { setLoader, setDelay, setNavigate, setTier } from '../redux/actions/shared'
import { personData, updatePersonData, validatePersonData } from '../redux/actions/personData'
import { getRejectedFiles, getUserImages, sendCheckUserTier, saveUserImage } from '../redux/actions/files'
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TIER_SET_SCHEMA, TIER_STEPS } from '../redux/types/tier';
import { sendPostRequest } from '../services/baseApi';
import { useParams } from 'react-router-dom';
import { BASE_AUTOLOGIN_URL, BASE_IBV } from '../redux/types/base';
import { setIBV } from '../redux/actions/base';
import { useRegisterIpLogMutation } from '../redux/services/services';
import { getAproxUserGeolocation } from '../redux/actions/base';
import { getDevice } from '../helpers/geolocation';
import { isLendecyApp } from '../helpers/base';

/**
 * hook to init in Form Component
 * @param {Object} tierFieldState
 * @returns
 */
export const useInitFormTier = tierFieldState => {
  const dispatch = useDispatch();
  const { loading } = useSelector(state => state.base);
  const { action, schema } = useSelector(state => state.tier);
  const [schemaYup, setSchemaYup] = useState({});
  const { codeTier } = useParams();

  useEffect(() => {
    // setTimeout(() => {
      if (tierFieldState.length > 0) {
        if (isUndefinedNull(schema)) {
          const { schemaTier, defaultValues } = getSchemaTier(
            action, tierFieldState, codeTier);
          setSchemaYup(schemaTier);
          dispatch({
            type: TIER_SET_SCHEMA,
            payload: {
              schema: { schema: schemaTier, defaultValues },
              defaultValuesTier: defaultValues
            }
          });
        } else {
          const { schemaTier } = getSchemaTier(
            action, tierFieldState, codeTier);
          setSchemaYup(schemaTier);
        }
      }
    // }, 10);
  }, [schema, tierFieldState, action, codeTier, dispatch]);

  return { action, loading, schemaTier: schemaYup };
};

/**
 * Hook to form
 * @param {Object} tier
 * @param {Object} tierFieldState
 * @param {Object} categoriesTier
 * @param {Object} schemaTier
 * @returns
 */
export const useFormTier = (tier, tierFieldState, categoriesTier, schemaTier) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { defaultValuesTier, highstStep } = useSelector(state => state.tier);
  const methods = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(yup.object(schemaTier))
  });
  const { requestLanguage } = useSelector(state => state.base);
  const { tier: allTiers } = useSelector(state => {
    const data = state.staticData[`data_${trimLowerCase(requestLanguage)}`];
    return data || {};
  });
  const [hideCategories, setHideCategories] = useState([]);
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    let hideCategories = moduleCategories(categoriesTier, tierFieldState);
    setHideCategories(hideCategories);
    let userTierRes = userTier()
    defaultChecked(userTierRes);
  }, [categoriesTier, tierFieldState]);

  // Effect runs when user state is updated
  useEffect(() => {
    // Reset form
    methods.reset(defaultValuesTier);
  }, [defaultValuesTier]);

  useEffect(() => {
    const matchingDisabledTiers = allTiers.filter(item =>
      trimLowerCase(item.unique_name) === trimLowerCase(tier.unique_name) &&
      item.disabled
    );
    if (allTiers && tier && matchingDisabledTiers.length > 0 && matchingDisabledTiers[0].unique_name !== slimTier) { // Condition to FORCEFULY avoid redirection on tier slim advanced
      navigate(siteMap.homepage.path);
    }
  }, [allTiers, tier]);

  useEffect(() => {
    if ((highstStep === 0 && hideCategories.length > 0) || steps.length === 0) {
      let newSteps = (hideCategories?.filter(item => trimLowerCase(
        item.unique_name) !== baseField.modalValidateSms).map(
          item => {
            if (item?.active) return item.step
            return null
          }).filter((c, index) => {
            return hideCategories?.filter(item => trimLowerCase(
              item.unique_name) !== baseField.modalValidateSms).map(
                item => item.step).indexOf(c) === index;
          }));
      setSteps(newSteps);
      dispatch({
        type: TIER_STEPS,
        payload: (newSteps.length !== 0) ? Math.max(...newSteps) : 1
      });
    }
  }, [highstStep, hideCategories, steps]);

  const onRouteProfile = () => navigate(routes.profile('profile'));

  return { hideCategories, methods, onRouteProfile, steps };
};

/**
 * Hook to action in form
 * @param {Object} hideCategories
 * @param {Object} methods
 * @param {Object} tier
 * @param {Object} tierFieldState
 * @returns
 */
export const useFormSend = (hideCategories, methods, tier, tierFieldState, steps, handleOpenIbv) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { nextTier, highstStep, lastTier, action, tiers } = useSelector(state => state.tier);
  const current_tier_level = tier?.verification_level || null
  const { allowedCountries, location, autoLoginURL } = useSelector(state => state.base);
  const { 
    data: { id: userId, user_contact: userContact, 
        product_request: { 
          user: userInfo 
        } = {}, bank_references: bankReferences  
      } = {}
    } = useSelector(state => state.personData) || {};
  const { requestLanguage, geolocation, deviceInfo } = useSelector(state => state.base)
  const { 
    country: countries,
    bank: banks,
    account_type: accountTypes
  } = useSelector(state => state.staticData[`data_${trimLowerCase(requestLanguage)}`]);
  const {
    userIdBifrost,
    lastProductRequestId,
    dataProductRequest,
    appOrigin
  } = useSelector(state => state.user);
  const currentProduct = dataProductRequest?.filter(product => Number(product.id) === Number(lastProductRequestId))[0]

  const [openModal, setOpenModal] = useState(1); // Number that controls the SMS TOKEN modal.
  const [activeGroup, setActiveGroup] = useState(1);
  // * Here we move the user to the 'bank_references' step (step '4'), if login via 'autoLoginURL'
  const [activeGroupIndex, setActiveGroupIndex] = useState(autoLoginURL?.active ? autoLoginURL.goToStep : 0) // Number to control the Step in the Tier
  const [sendTier, setSendTier] = useState(true);
  const [modalIsOpen, setModalIsOpen] = useState(false); // Boolean that controls the QUESTION modal.
  const [modalMessage, setModalMessage] = useState('');
  const [finishChanges, setFinishChanges] = useState(false);
  const { formState: { errors } } = methods;
  const { isIBVVerified, isIBVRequired, requestWidgetURL } = useSelector(state => state.base.ibv) || {}

  useEffect(() => {
    setActiveGroup(steps[activeGroupIndex])
  }, [steps])


/**
 * Registers the IP log by sending a POST request with geolocation and device information.
 *
 * @param {string} token - The authentication token required for the request.
 * @returns {Promise<Object>} - The response from the server.
 * @throws Will log an error to the console if the request fails.
 */
  const registerIpLog = async (token) => {
    let response
    try {
      let ipInfo = {
        ...geolocation,
        ...deviceInfo
      }
      if (!geolocation) {
        const geolocationResult = await dispatch(getAproxUserGeolocation());
        ipInfo = {
          ...ipInfo,
          ...geolocationResult,
        }
      }
      if (!deviceInfo) {
        const deviceDetails = await getDevice();
        ipInfo = {
          ...ipInfo,
          ...deviceDetails
        }
      }
      let data = { token, ip_info: ipInfo }
      response = registerIpLogMutation(data)
    } catch (error) {
      console.error('registerIpLog in useFormTier: ', error);
    }

    return response
  }

  /* Deactivate autologin's redirection and Register Ip Log whenever a user autologins */
  useEffect(() => {
      if (autoLoginURL?.active) {
        dispatch({ type: BASE_AUTOLOGIN_URL, payload: {
          active: false,
          goToStep: 0
        }})
      }
  }, [autoLoginURL?.active])

  /* Perform call to register-ip-log in the first render after user's autologin */
  const [registerIpLogMutation] = useRegisterIpLogMutation();
  useEffect(() => {
    if (autoLoginURL?.token?.length){
      registerIpLog(autoLoginURL.token)
      dispatch({ type: BASE_AUTOLOGIN_URL, payload: {
        token: ''
      }})
    }

  }, [autoLoginURL?.token])

  const getUniqueNameByValueKey = key => key.split('__id__').shift();

  const onBackStep = () => {
    setActiveGroupIndex(prev => {
      const newIndex = prev - 1
      setActiveGroup(steps[newIndex])
      return newIndex
    })
  }

  const reUpdatePersonData = async () => {
    await dispatch(updatePersonData(userIdBifrost, lastProductRequestId)) 
    await dispatch(getUserImages(userIdBifrost))
  }

  const onSubmit = async values => {
    try {
      const formObject = validateRequestType(values, tierFieldState, userId);
      const allowed = validateCountry(formObject.elements, allowedCountries, tier.unique_name);

      removeFromFormObject(bankReferencesField, 1, methods, formObject, bankReferencesElement);
      if (allowed) {
        // Leave this comparison, since it can get an array
        if (hideCategories.filter(
          category => trimLowerCase(
            category.unique_name) === baseField.modalValidateSms).length > 0) {
          setOpenModal(openModal + 1);
          setActiveGroup(1);
          setModalIsOpen(false);
        } else {
          let saveFormMedia = true;
          const migratable = tierMigratable(
            formObject.migratable, tier, finishChanges);
          if (formObject.isElement || formObject.isMedia) {
            dispatch(setLoader(true));
            formObject.elements['country_code'] = location;
            const response = await dispatch(personData(
              formObject, userIdBifrost, lastProductRequestId, true));
            if (response && !response.error && !response.client_error) {
              if (nextTier?.verification_level) {
                if (tier.verification_level > nextTier.verification_level) dispatch(
                  setTier(tier));
              } else {
                dispatch(setTier(tier));
              }
              if (formObject.isMedia && !isLendecyApp()) {
                dispatch(setDelay(true));
                saveFormMedia = await dispatch(saveUserImage(
                  userIdBifrost, formObject.formMedia, false, false));
              }
              if (finishChanges && !migratable?.skipLastTier) {
                if (saveFormMedia) {
                  const result = await dispatch(migrateBifrost( 
                    userIdBifrost, migratable, userId, lastProductRequestId));
                  if (result && !result.error) {
                    if (migratable.migrate){
                      // This call currently reupdates all of the tiers (intermediate, deeper and advanced) /
                      setTimeout(() => reUpdatePersonData(), 20000)
                    } 
                    if (await dispatch(updatePersonData(userIdBifrost, lastProductRequestId))) {
                      await dispatch(sendCheckUserTier());
                      await dispatch(getUserImages(userIdBifrost))
                      dispatch(getRejectedFiles());
                      if (finishChanges) {
                        navigate(routes.completed(paramsFinish));
                      } else {
                        navigate(routes.completed(paramsNext));
                      }
                      dispatch(setLoader(false));
                      dispatch(setDelay(false));
                    }
                  } else {
                    dispatch(setNavigate(false));
                    dispatch(setLoader(false));
                    dispatch(setDelay(false));
                  }
                } else {
                  dispatch(setNavigate(false));
                  dispatch(setLoader(false));
                  dispatch(setDelay(false));
                }
              } else {
                if (finishChanges && migratable?.skipLastTier) {
                  // If skipping last tier, migrate now
                  const result = await dispatch(userFilesAndMigrateBifrost(
                    userIdBifrost, migratable, userId, lastProductRequestId, formObject.formMedia))
                  if (result && !result?.error) {
                    if (migratable.migrate){
                      // This call currently reupdates all of the tiers (intermediate, deeper and advanced) /
                      setTimeout(() => reUpdatePersonData(), 20000)
                    } 
                  }
                  if (await dispatch(updatePersonData(userIdBifrost, lastProductRequestId))) {
                    await dispatch(sendCheckUserTier());
                    await dispatch(getUserImages(userIdBifrost))
                    dispatch(getRejectedFiles());
                    if (finishChanges) {
                      navigate(routes.completed(paramsFinish));
                    } else {
                      navigate(routes.completed(paramsNext));
                    }
                    dispatch(setLoader(false));
                    dispatch(setDelay(false));
                  }
                } else {
                  if (saveFormMedia) {
                    const result = await dispatch(migrateBifrost( 
                      userIdBifrost, migratable, userId, lastProductRequestId));
                    if (result && !result.error) {
                      if (migratable.migrate){
                        // This call currently reupdates all of the tiers (intermediate, deeper and advanced) /
                        setTimeout(() => reUpdatePersonData(), 20000)
                      } 
                      if (await dispatch(updatePersonData(userIdBifrost, lastProductRequestId))) {
                        await dispatch(sendCheckUserTier());
                        await dispatch(getUserImages(userIdBifrost))
                        dispatch(getRejectedFiles());
                        if (finishChanges) {
                          navigate(routes.completed(paramsFinish));
                        } else {
                          navigate(routes.completed(paramsNext));
                        }
                        dispatch(setLoader(false));
                        dispatch(setDelay(false));
                      }
                    } else {
                      dispatch(setNavigate(false));
                      dispatch(setLoader(false));
                      dispatch(setDelay(false));
                    }
                  } else {
                    dispatch(setNavigate(false));
                    dispatch(setLoader(false));
                    dispatch(setDelay(false));
                  }
                }
              }

            } else {
              if (!nextTier?.verification_level) dispatch(setTier([]));
              dispatch(setLoader(false));
              dispatch(setDelay(false));
            }
          } else {
            if (finishChanges || migratable?.skipLastTier) {
              const slimTierAdvanced = tiers[slimTier]?.data
              dispatch(setLoader(true));
              const migratableObject = {
                'migrate': true,
                'edit': true,
                'tier': migratable?.skipLastTier ? slimTierAdvanced : lastTier
              };
              const result = await dispatch(migrateBifrost(
                userIdBifrost, migratableObject, userId, lastProductRequestId));
              if (result && !result.error) {
                if (migratableObject.migrate){
                  // This call currently reupdates all of the tiers (intermediate, deeper and advanced) /
                  setTimeout(() => reUpdatePersonData(), 20000)
                } 
                dispatch(setLoader(false));
                navigate(routes.completed(paramsFinish));
              } else {
                dispatch(setLoader(false));
              }

            } else {
              navigate(routes.completed(paramsNext));
            }
          }
        }
      } else {
        dispatch(setMessage(t("common.countryNotAllowed"), 'error'));
      }
      } catch (error) {
      console.error('onSubmit in useFormTier: ', error);
      dispatch(setMessage(t("message.errorFormTier"), 'error'));
    }
  };

  /* Obtain required info for IBV process and Obtain request code  */
  const obtainWidgetUrl = async (values, tierSlim = false) => {
    dispatch(setLoader(true))
    let ibvData = {}
    if (!tierSlim) { // Normal path, to be used in the 'advanced' tier
      let bankCode = Object.entries(values).find(([key]) => key.startsWith(IBVSetup.bankName))[1];
      let bank = banks.filter(bank => Number(bank.bank_id) === Number(bankCode))[0]
      let abaCode = Object.entries(values).find(([key]) => key.startsWith(IBVSetup.abaCode))[1];
      let accountNumber = Object.entries(values).find(([key]) => key.startsWith(IBVSetup.accNumber))[1];
      let accountTypeId = Object.entries(values).find(([key]) => key.startsWith(IBVSetup.accType))[1];
      let accountType = accountTypes.filter(type => Number(type?.account_type_id) === Number(accountTypeId))[0]
      let [userPhone] = userContact.filter(userContact => userContact.main)
      let country = countries.filter(country => country?.country_id === userPhone?.country_id || country?.codeId === userPhone?.country_id || country?.idValue === userPhone?.country_id)[0]
      let userPhoneNumber = country?.phone_code + userPhone?.description

      ibvData = {
        "bifrost_user_id": userIdBifrost,
        "bank_name": bank?.description || bankCode,
        "account_number": accountNumber,
        "aba_code": abaCode,
        "account_type": accountType?.description,
        "cellphone": userPhoneNumber
      }
    }

    if (tierSlim) { // Path to be taken in the tier Slim
      let bankRef = bankReferences?.[0]
      let bankCode = bankRef?.['bank_name']
      let abaCode = bankRef?.['aba_code']
      let accountNumber = bankRef?.['account_number']
      let accountTypeId = bankRef?.['client_account_type_id']
      let accountType = accountTypes.filter(type => Number(type?.account_type_id) === Number(accountTypeId))[0]
      let [userPhone] = userContact.filter(userContact => userContact.main)
      let country = countries.filter(country => country?.country_id === userPhone?.country_id || country?.codeId === userPhone?.country_id || country?.idValue === userPhone?.country_id)[0]
      let userPhoneNumber = country?.phone_code + userPhone?.description

      ibvData = {
        "bifrost_user_id": userIdBifrost,
        "bank_name": bankCode,
        "account_number": accountNumber,
        "aba_code": abaCode,  
        "account_type": accountType?.description,
        "cellphone": userPhoneNumber
      }
    }

    let res = await sendPostRequest(ibvData, endpoints?.requestChirpCode)
    if (res?.success && !res?.error) {
      let {verification_url} = res?.success?.data
      dispatch({
        type: BASE_IBV,
        payload: {
          isIBVShown: true,
          isIBVRequired: true,
          requestWidgetURL: verification_url
        }
      })
      setTimeout(() => {
        dispatch(setMessage(t("ibv.ibvRequired"), 'info'));
      }, 300);
    } else {
      setTimeout(() => {
        dispatch(setMessage(t("ibv.ibvRequestCodeError"), 'error'));
      }, 300)
    }
    dispatch(setLoader(false))
  }

  const onContinueStep = async () => {
    window.scrollTo(0, 0)
    const values = methods.getValues();
    const tierFieldStateByStep = tierFieldState.filter(
      item => (item.step === activeGroup) && (
        trimLowerCase(item.unique_name) !== baseField.modalValidateSms));
    const tierStepFile = tierFieldState.filter(
      item => (item.step === activeGroup) && (
        trimLowerCase(item.group) === baseField.validateGroupFile));
    const isSendTier = tierFieldState.filter(item => {
      if (activeGroup > 1) {
        return (item.step === activeGroup) && (trimLowerCase(
          item.group) === baseField.sendStepTier);
      } else {
        return true;
      }
    });
    setSendTier(isSendTier);

    const valuesArray = values ? Object.keys(values)
      .map(function (key) {
        const [selectedField] = tierFieldStateByStep?.filter(
          item => trimLowerCase(item.unique_name) === trimLowerCase(
            getUniqueNameByValueKey(key)));
        return selectedField?.id ? { value: values[key], ...selectedField } : {};
      }).filter(item => item)
      : null;

    let listMediaFields;
    let totalFilesSize = 0;
    if (tierStepFile.length > 0) {
      listMediaFields = tierFieldStateByStep?.filter(item =>
        item.data_type === 'media_upload' &&
        item.group === 'file' &&
        item.hasOwnProperty('input_file')
      );

      totalFilesSize = listMediaFields.reduce(
        (previousValue, currentValue) =>
          previousValue + currentValue.file_size, totalFilesSize
      );

      if (totalFilesSize > envSetting.maxTotalFilesSize) {
        dispatch(setMessage(t("files.errors.totalSumMaxFilesSize",
          { size: fileSumSizeLimit }), 'error'));
      }
    }

    const validateArray = valuesArray.filter(item => {
      const isInCurrentStep = item.step === activeGroup;
      const isRequired = item.required;
      const isEmptyValue = item.value !== false && !item.value;
      const isNotHidden = !isModuleHidden(item);
      const isEmptyArray = Array.isArray(item) && item?.length === 0;
      const isInvalidUrl = trimLowerCase(item.data_type) === 'url' && !validURL(item.value);
      const isInvalidEmail = trimLowerCase(item.data_type) === 'email' && !validateEmail(item.value);
      const isInvalidLength = validateLength(item.unique_name, item.id);
    
      return isInCurrentStep && isRequired && (
        (isEmptyValue && isNotHidden) ||
        isEmptyArray ||
        isInvalidUrl ||
        isInvalidEmail ||
        isInvalidLength
      );
    });

    if (validateArray.length > 0) {
      dispatch(setMessage(t("common.missingFields"), 'error'));
    
      methods.trigger();
    } else {
      let validated_ibv = currentProduct?.validated_ibv
      let productRequiresIBV = isLendecyApp()
      if ( 
          (!productRequiresIBV || !isIBVRequired || (isIBVRequired && isIBVVerified) || (activeGroup !== IBVSetup.stepToEvaluate) || validated_ibv)
      ) { // Happy path; increment step.
        if (highstStep > 1) {
          methods.clearErrors();
          if (activeGroupIndex < steps.length) {
            setActiveGroupIndex((prev) => {
              const newIndex =  prev+1
              setActiveGroup(steps[newIndex])
              return newIndex
            })
          }
          if(action === paramsAdd){
            const formObject = validateRequestType(values, tierFieldStateByStep, userId);
            removeFromFormObject(bankReferencesField, 1, methods, formObject, bankReferencesElement);
            const validatedElements = validatePersonData(formObject.elements)
            validatedElements['country_code'] = location;
            await sendPostRequest(validatedElements, endpoints.personData(userIdBifrost, lastProductRequestId))
          }
        }; 
      } else { // Else, we must verify IBV
        methods.clearErrors();
        if(requestWidgetURL?.length === 0 && !validated_ibv){
          await obtainWidgetUrl(values)
        } else {
          setTimeout(() => {
            dispatch(setMessage(t("ibv.ibvRequired"), 'info'));
          }, 300);
        }
      }
    }

  };

  /* Automatically submitting form after IBV */
  useEffect(() => {
    setTimeout(() => {
      if ((requestWidgetURL?.length > 0) && isIBVVerified) {
        /* Clickling the Opt-In modal button, which, given the if condition we are in, should submit the Form (by calling to 'onChangeStep') */
        let btn = document.getElementsByName('btn_modal')[0]
        btn?.click()
      }
    }, 600);
  }, [isIBVVerified, requestWidgetURL?.length])

  /* Could as well be called 'onChangeTier' */
  const onChangeStep = () => {
    if (activeGroup > 1) {
      if (highstStep === activeGroup) {
        setSendTier(true);
      }
    }
    const values = methods.getValues();
    onSubmit(values);
  };

  const getTrigger = async () => {
    methods.clearErrors();
    const trigger = await methods.trigger();
    if (!trigger) {
      dispatch(setMessage(t("common.missingFields"), 'error'));
      return trigger;
    }
    return trigger;
  };

  /* On last step; triggers Opt-in, in last tier. */
  const onSave = async () => {
    const conditionalStates = envSetting?.conditionalStates.split(',')
    const isLendecy = isLendecyApp()
    const values = methods.getValues();
    if (await getTrigger()) {
      // const [productRequest] = Array.from(dataProductRequest).filter(item => item.id === lastProductRequestId)

      // If its the last tier
      if (Number(current_tier_level) === Number(lastTier?.verification_level) ||
        (Number(current_tier_level) === (Number(lastTier?.verification_level) - 1) &&
        (conditionalStates.includes(trimLowerCase(userInfo?.state)) && isLendecy))
      ) {
        // If tier is not 'slim_advanced' 
        if (tier?.unique_name !== slimTier) {
            setFinishChanges(true)
        } else { // if tier is 'slim_advanced
            let validated_ibv = currentProduct?.validated_ibv
            if (isIBVVerified || validated_ibv) {
              // Advance, if IBV is verified
              setFinishChanges(true)
              const params = {
                pendingVerification: true
              }
              dispatch(setIBV(params))
            } else {
              // If not verified, either obtain widget URL or show message
              if(requestWidgetURL?.length === 0 && !validated_ibv){
                await obtainWidgetUrl(values, true)
              } else {
                // const params = {
                  // pendingVerification: true
                // }
                // dispatch(setIBV(params)) 
                handleOpenIbv()
              }
            }
        }
      } else {
        // Rest of the tiers
        setModalMessage(t("message.modalConfirmation"));
        setModalIsOpen(true)
      }
    }
  };

  const onFinishRequest = async () => {
    if (await getTrigger()) {
      setModalMessage(t("message.modalFinishRequest"));
      setModalIsOpen(true);
      setFinishChanges(true);
    }
  };

  return {
    activeGroup,
    highstStep,
    lastTier,
    modalIsOpen,
    modalMessage,
    onBackStep,
    onChangeStep,
    onContinueStep,
    onFinishRequest,
    onSave,
    onSubmit,
    openModal,
    setOpenModal,
    sendTier,
    setModalIsOpen,
    setFinishChanges,
    finishChanges
  };
};
